import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from 'styled-components';
import ReactTooltip from 'react-tooltip';
import { useSelector, useDispatch } from 'react-redux';
import UpdateUSAddonModal from 'products/nomad_insurance/components/UpdateUSAddonModal';
import { addonChange } from 'products/nomad_insurance/views/Purchase/steps/Coverage/state';
import {
  FORBIDDEN_DESTINATIONS_SW,
  FORBIDDEN_DESTINATIONS_SW_FOR_US_USERS,
  US_ADDON_REQUIRED_DESTINATIONS_SW
} from 'products/nomad_insurance/constants';
import Chip from 'components/Chip';
import useIsUSPlanholder from 'products/nomad_insurance/hooks/useIsUSPlanholder';
import InputErrorMessage from 'products/nomad_insurance/components/InputErrorMessage';
import { COUNTRY_OPTIONS } from 'products/nomad_insurance/components/Select/consts';
import { setDestinations, setValidity } from './state';
import { MapContainer, DestinationList, CountrySelect, TooltipContent } from './styled';
import WorldMap from './components/WorldMap';
import HomeCountryWarning from './components/HomeCountryWarning';
import Exclusions from './components/Exclusions';
import USGroupMemberWarning from '../../components/USGroupMemberWarning';
import Accordion from '../../components/Accordion';
import StepView from '../../components/StepView';
import { useStepNavigationContext } from '../../context';
import { Title, InfoText, MediumText, NextRow, NextButton } from '../../styled';
import { trackHubSpotEvent } from '../../utils/hubspot/hubspot-custom-events';

const Destinations = () => {
  const dispatch = useDispatch();
  const { changeStep, jumpToFirstStep, isRenewal } = useStepNavigationContext();
  const [isUSAddonModalOpen, setIsUSAddonModalOpen] = useState(false);
  const theme = useTheme();
  const [mapTooltip, setMapTooltip] = useState('');
  const [showUSGroupMemberWarning, setShowUSGroupMemberWarning] = useState(false);
  const [ineligibleCountry, setIneligibleCountry] = useState(null);
  const [homeCountryWarning, setHomeCountryWarning] = useState(null);
  const {
    data: userProfile,
    citizenshipCountry,
    groupMembers
  } = useSelector((state) => state.app.profile);
  const { isHomeCountryUS, isCitizenshipUS } = useIsUSPlanholder();
  const { addons } = useSelector(
    (state) => state.nomadInsurance.purchaseInsurance.coverage
  );
  const { destinations } = useSelector(
    (state) => state.nomadInsurance.purchaseInsurance.destinations
  );
  const { isValid } = useSelector(
    (state) => state.nomadInsurance.purchaseInsurance.basicInfo
  );
  const { activePaidContracts } = useSelector((state) => state.nomadInsurance.contract);

  const { data: planholder } = useSelector((state) => state.app.profile);

  const forbiddenDestinations = useMemo(() => {
    if (isHomeCountryUS) {
      return FORBIDDEN_DESTINATIONS_SW_FOR_US_USERS;
    }
    return FORBIDDEN_DESTINATIONS_SW;
  }, [isHomeCountryUS]);

  useEffect(() => {
    if (!isValid) {
      jumpToFirstStep();
    }
  }, [isValid, jumpToFirstStep]);

  const resetWarnings = () => {
    setIneligibleCountry(false);
    setHomeCountryWarning(false);
  };

  useEffect(() => {
    const hasDestinations = destinations && destinations.length > 0;
    const hasContract = activePaidContracts && activePaidContracts.length > 0;
    if (isRenewal && hasContract && !hasDestinations) {
      const currentContract = activePaidContracts[0];
      const destinations = currentContract.destinationISOCountries;
      dispatch(setDestinations(destinations));
    }
  }, [isRenewal, activePaidContracts, dispatch]);

  useEffect(() => {
    if (userProfile?.homeCountry && destinations?.includes(userProfile?.homeCountry)) {
      dispatch(
        setDestinations(destinations.filter((x) => x !== userProfile?.homeCountry))
      );
    }
  }, [userProfile?.homeCountry, dispatch]); // eslint-disable-line

  const isIneligibleDestination = (code) => {
    if (forbiddenDestinations.includes(code)) {
      setIneligibleCountry(code);
      return true;
    }
    return false;
  };

  const onWorldMapChange = (code) => {
    resetWarnings();
    if (!destinations.includes(code)) {
      if (isIneligibleDestination(code)) return;
      if (code === 'US') {
        if (
          groupMembers?.find(
            (groupMember) =>
              groupMember.citizenshipCountry === 'US' || groupMember.citizenship === 'US'
          )
        ) {
          setShowUSGroupMemberWarning(true);
          return;
        }
        setIsUSAddonModalOpen(true);
        return;
      }

      dispatch(setDestinations([...destinations, code]));
    }
  };

  const onAddCountry = (country) => {
    resetWarnings();
    if (!destinations.includes(country.value)) {
      if (isIneligibleDestination(country.value)) return;

      if (country.value === userProfile?.homeCountry) {
        setHomeCountryWarning(true);
        return;
      }

      if (
        (isCitizenshipUS || isHomeCountryUS) &&
        FORBIDDEN_DESTINATIONS_SW_FOR_US_USERS.includes(country.value)
      ) {
        return;
      }

      if (US_ADDON_REQUIRED_DESTINATIONS_SW.includes(country.value)) {
        if (
          groupMembers?.find(
            (groupMember) =>
              groupMember.citizenshipCountry === 'US' || groupMember.citizenship === 'US'
          )
        ) {
          setShowUSGroupMemberWarning(true);
          return;
        }
        setIsUSAddonModalOpen(true);
        return;
      }

      dispatch(setDestinations([...destinations, country.value]));
    }
  };

  const onDeleteCountry = (code) => {
    if (
      US_ADDON_REQUIRED_DESTINATIONS_SW.includes(code) &&
      US_ADDON_REQUIRED_DESTINATIONS_SW.filter((destination) =>
        destinations.includes(destination)
      ).length === 1
    ) {
      setIsUSAddonModalOpen(true);
      return;
    }

    const arr = [...destinations];
    const index = arr.indexOf(code);
    arr.splice(index, 1);
    dispatch(setDestinations(arr));
  };

  const changeAddon = (addon) => {
    if (addon.value === 'USA_COVERAGE') {
      if (
        !US_ADDON_REQUIRED_DESTINATIONS_SW.some((destination) =>
          destinations.includes(destination)
        )
      ) {
        dispatch(
          setDestinations([...destinations, ...US_ADDON_REQUIRED_DESTINATIONS_SW])
        );
      } else {
        dispatch(
          setDestinations(
            destinations.filter((x) => !US_ADDON_REQUIRED_DESTINATIONS_SW.includes(x))
          )
        );
      }
    }
    dispatch(addonChange(addon));
  };

  const navigateToNextStep = () => {
    dispatch(setValidity(true));

    void trackHubSpotEvent('destinations', planholder.email);

    changeStep(1);
  };

  useEffect(() => {
    if (
      addons.find((x) => x.value === 'USA_COVERAGE') &&
      !US_ADDON_REQUIRED_DESTINATIONS_SW.some((destination) =>
        destinations.includes(destination)
      )
    ) {
      dispatch(setDestinations([...destinations, ...US_ADDON_REQUIRED_DESTINATIONS_SW]));
    }
  }, [dispatch]); //eslint-disable-line

  return (
    <>
      {showUSGroupMemberWarning && (
        <USGroupMemberWarning
          isOpen={!!showUSGroupMemberWarning}
          onClose={() => setShowUSGroupMemberWarning(false)}
          groupMembers={groupMembers}
        />
      )}
      {isUSAddonModalOpen && (
        <UpdateUSAddonModal
          isOpen={isUSAddonModalOpen}
          onClose={() => setIsUSAddonModalOpen(false)}
          addonChange={changeAddon}
        />
      )}
      <StepView>
        <Title>Where are you going?</Title>
        <InfoText>
          Nomad Insurance Essential covers you across 170 countries worldwide. We{' '}
          <MediumText>only need your first destination</MediumText> to activate your
          global policy, but recommend adding all planned destinations in case of visa
          letter requirements. Your home country cannot be a destination.
        </InfoText>
        <Accordion heading="Learn more about exclusions">
          <Exclusions homeCountry={userProfile?.homeCountry} />
        </Accordion>

        <CountrySelect
          showFlag
          hideSelection
          showIndicator
          label="Add a country"
          options={COUNTRY_OPTIONS}
          name="country"
          onChange={onAddCountry}
          hasError={ineligibleCountry || homeCountryWarning}
          variant="gray"
        />
        {ineligibleCountry && (
          <InputErrorMessage errorMessage="Sorry, we can't offer coverage in the selected country at this time." />
        )}
        {homeCountryWarning && <HomeCountryWarning />}
        <DestinationList>
          {destinations
            .slice()
            .reverse()
            .map((country) => (
              <Chip
                countryCode={country}
                key={country}
                onDelete={() => onDeleteCountry(country)}
              />
            ))}
        </DestinationList>
        <MapContainer>
          <WorldMap
            setTooltipContent={setMapTooltip}
            countries={destinations}
            onChange={onWorldMapChange}
            homeCountry={userProfile?.homeCountry}
            citizenshipCountry={citizenshipCountry}
          />
          <ReactTooltip
            place="bottom"
            tooltipRadius="16"
            backgroundColor={theme.colors.pickledBluewood}
            arrowColor="transparent"
          >
            {mapTooltip && <TooltipContent>{mapTooltip}</TooltipContent>}
          </ReactTooltip>
        </MapContainer>

        <NextRow>
          <NextButton
            disabled={destinations?.length <= 0}
            color="confetti"
            onClick={navigateToNextStep}
          >
            Next
          </NextButton>
          <InfoText>Next up, add group members</InfoText>
        </NextRow>
      </StepView>
    </>
  );
};

export default Destinations;
